import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSpinner,
  faCalendarAlt,
  faTag,
  faDoorOpen,
  faIdCard,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../context/ThemeContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import styles from './RoomManagementModal.module.css';

const RoomManagementModal = ({ isOpen, onClose, onSave, initialRoom, hotelCheckInDate, hotelCheckOutDate }) => {
  const [room, setRoom] = useState({
    name: '',
    confirmationNumber: '',
    roomNumber: '',
    bagTag: '',
    checkInDate: hotelCheckInDate ? new Date(hotelCheckInDate) : new Date(),
    checkOutDate: hotelCheckOutDate ? new Date(hotelCheckOutDate) : new Date(),
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Get theme from context
  const { isDark } = useTheme();

  useEffect(() => {
    if (initialRoom) {
      setRoom({
        ...initialRoom,
        checkInDate: initialRoom.checkInDate ? new Date(initialRoom.checkInDate) : new Date(),
        checkOutDate: initialRoom.checkOutDate ? new Date(initialRoom.checkOutDate) : new Date(),
      });
    } else {
      setRoom({
        name: '',
        confirmationNumber: '',
        roomNumber: '',
        bagTag: '',
        checkInDate: hotelCheckInDate ? new Date(hotelCheckInDate) : new Date(),
        checkOutDate: hotelCheckOutDate ? new Date(hotelCheckOutDate) : new Date(),
      });
    }
  }, [initialRoom, hotelCheckInDate, hotelCheckOutDate]);

  const handleChange = (field, value) => {
    setRoom(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    if (!room.name.trim()) {
      alert('Please enter a name');
      return;
    }

    setIsLoading(true);
    
    try {
      onSave({
        ...room,
        checkInDate: room.checkInDate.toISOString(),
        checkOutDate: room.checkOutDate.toISOString(),
      });
    } catch (error) {
      console.error('Error saving room:', error);
      alert('Failed to save room. Please try again.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles.modalOverlay} ${isDark ? styles.darkTheme : ''}`} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{initialRoom ? 'Edit Room' : 'Add Room'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            <FontAwesomeIcon icon={faUser} className={styles.inputIcon} />
            Name
          </label>
          <input
            type="text"
            className={styles.textInput}
            value={room.name}
            onChange={e => handleChange('name', e.target.value)}
            placeholder="Enter who this room is for"
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            <FontAwesomeIcon icon={faIdCard} className={styles.inputIcon} />
            Confirmation Number
          </label>
          <input
            type="text"
            className={styles.textInput}
            value={room.confirmationNumber}
            onChange={e => handleChange('confirmationNumber', e.target.value)}
            placeholder="e.g., CONF123456"
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            <FontAwesomeIcon icon={faDoorOpen} className={styles.inputIcon} />
            Room Number
          </label>
          <input
            type="text"
            className={styles.textInput}
            value={room.roomNumber}
            onChange={e => handleChange('roomNumber', e.target.value)}
            placeholder="e.g., 301"
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.inputLabel}>
            <FontAwesomeIcon icon={faTag} className={styles.inputIcon} />
            Bag Tag
          </label>
          <input
            type="text"
            className={styles.textInput}
            value={room.bagTag}
            onChange={e => handleChange('bagTag', e.target.value)}
            placeholder="e.g., RED-123"
          />
        </div>

        <div className={styles.dateContainer}>
          <div className={styles.dateGroup}>
            <label className={styles.dateLabel}>
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.inputIcon} />
              Check-In Date
            </label>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={room.checkInDate}
                onChange={date => handleChange('checkInDate', date)}
                className={styles.datePicker}
                dateFormat="MMMM d, yyyy"
                wrapperClassName={styles.datePickerFullWidth}
              />
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
            </div>
          </div>

          <div className={styles.dateGroup}>
            <label className={styles.dateLabel}>
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.inputIcon} />
              Check-Out Date
            </label>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={room.checkOutDate}
                onChange={date => handleChange('checkOutDate', date)}
                className={styles.datePicker}
                dateFormat="MMMM d, yyyy"
                wrapperClassName={styles.datePickerFullWidth}
              />
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
            </div>
          </div>
        </div>

        <div className={styles.actionButtons}>
          <button 
            className={styles.cancelButton} 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            className={styles.saveButton} 
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> 
                Saving...
              </>
            ) : (
              'Save Room'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomManagementModal;